import { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setScreenshotPresets } from "store/features/userSettings/userSettingsSlice";

import { getScreenshotPreset, getUserOrgScreenshots } from "../api/users";
import { ErrorBoundary } from "../components/base";
import PopoutChart from "../components/charts/components/PopoutChart";
import DashboardWrapper from "../components/dashboard/DashboardWrapper";
import { DashboardProvider } from "../components/dashboard/contexts/DashboardContext";
import { GeomBinProvider } from "../components/geom-bin/contexts/GeomBinContext";
import { useGetCategoryList } from "../components/groupBy/hooks/useGetCategoryList";
import { IconSpinner } from "../components/icons";
import { ScreenshotProvider } from "../components/screenshot/contexts/ScreenshotContext";
import { ScreenshotPresetProvider } from "../components/screenshot/contexts/ScreenshotPresetContext";
import { SessionInfo } from "../components/user";
import { WellListProvider } from "../components/well-list/context";
import { WorkspaceProvider } from "../components/workspace/contexts/WorkspaceContext";
import { EntityKind } from "../models/entityKind";
import { RootState } from "../store/rootReducer";
import { useTxnIdCrossTabSubscriber } from "../stores/map";
import type { PopoutChannelState } from "../types/popout";
import { usePopoutForecastSync } from "./usePopoutForecastSync";
import { usePopoutInitialState } from "./usePopoutInitialState";

export default function PopoutChartPane() {
  const dispatch = useDispatch();

  const channelId = useSelector((state: RootState) => state.channel.channel);
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const entityKindStr = urlParams.get("entityKind");
  const entityKind = entityKindStr
    ? EntityKind[entityKindStr as keyof typeof EntityKind] || EntityKind.Well
    : EntityKind.Well;
  const categories = useSelector((state: RootState) => state.groupBy.categoryList);
  const presets = useSelector((state: RootState) => state.userSetting.screenshotPreset);
  const token = useSelector((state: RootState) => state.auth.jwtToken);
  const user = useSelector((state: RootState) => state.auth.user);
  const [isTokenSet, setIsTokenSet] = useState(false);

  const channelData = sessionStorage.getItem(channelId);
  const initialState: Partial<PopoutChannelState> | null = channelData
    ? JSON.parse(channelData)
    : null;

  // Forwards cross-tab redux-state-sync updates to the local ForecastStore.
  // See usePopoutForecastSync for details.
  usePopoutForecastSync();

  // Restores per-channel state captured at pop-out time from sessionStorage.
  usePopoutInitialState(entityKind, initialState);

  // Keeps the map txnId (legend items, counts, colours) live after that snapshot.
  useTxnIdCrossTabSubscriber();

  // fetch and update presets, if not present
  useEffect(() => {
    if (!user) return;
    if (presets.length) return;

    const userPresets = getScreenshotPreset();
    const orgPresets = getUserOrgScreenshots();
    Promise.all([orgPresets, userPresets])
      .then((values) => {
        dispatch(setScreenshotPresets([...values[0], ...values[1]]));
      })
      // eslint-disable-next-line no-console
      .catch(console.log);
  }, [dispatch, presets, user]);

  useEffect(() => {
    if (!isTokenSet && token) {
      setIsTokenSet(true);
    }
  }, [dispatch, isTokenSet, token]);

  // fetch presetList and categories, if not already in store
  const { getCategoryListWithUnit, isReady } = useGetCategoryList();
  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (isTokenSet && !categories.length) {
      // only get categories/presets if token is set
      getCategoryListWithUnit(EntityKind.Well);
    }
  }, [categories, isTokenSet, dispatch, isReady]);

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div
            style={{
              display: "grid",
              justifyContent: "center",
              alignItems: "center"
            }}>
            <div>
              Loading...
              <div>
                <IconSpinner />
              </div>
            </div>
          </div>
        }>
        <WorkspaceProvider>
          <DashboardProvider>
            <DashboardWrapper>
              <ScreenshotProvider>
                <ScreenshotPresetProvider>
                  <GeomBinProvider>
                    <WellListProvider>
                      <PopoutChart entityKind={entityKind} />
                    </WellListProvider>
                  </GeomBinProvider>
                </ScreenshotPresetProvider>
              </ScreenshotProvider>
            </DashboardWrapper>
          </DashboardProvider>
        </WorkspaceProvider>
      </Suspense>
      <SessionInfo />
    </ErrorBoundary>
  );
}
